import { ChevronLeft } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from '@/components/ui/sonner'
import { useLakeDocumentStatus } from '@/hooks/useLakeDocumentStatus'
import { type ExtractionScope, extractionScopeApi } from '@/services/api/extraction-scope'
import type {
  DocumentIndexedData,
  LakeDocument,
  ParquetSection,
  TabularColumn,
  TabularQueryRequest,
} from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { ExtractionScopeModal } from '../ExtractionScopeModal'
import { ProcessingStatsTab } from '../ProcessingStatsTab'
import { StructuralExtractionModal } from '../StructuralExtractionModal'
import { FileDetailHeader } from './FileDetailHeader'
import { ImportsTab } from './ImportsTab'
import { IndexedDataTab } from './IndexedDataTab'
import { OverviewTab } from './OverviewTab'
import { PageTitle } from './sections'
import type { DetailTab, ExtractionModalState } from './types'
import { STRUCTURAL_FILE_TYPES } from './utils'

interface LakeFileDetailProps {
  document: LakeDocument
  labelName: string
  labelColor: string | null
  onDocumentUpdate?: (document: LakeDocument) => void
  onBack?: () => void
  onDelete?: () => Promise<void>
  /** Called after a successful reprocess. Parent should invalidate any
      list/tree query that mirrors this document's state. */
  onReprocessed?: () => void
  activeTab: DetailTab
  onTabChange: (tab: DetailTab) => void
  /** Import-to-case mode (set when /data was opened with
      ?destination_case_id=<uuid>). Shows an "Import to case" action in the
      header and routes the modal submit back to the case workspace. */
  destinationCaseId?: string | null
  destinationCaseName?: string | null
  onImportSucceeded?: () => void
}

export function LakeFileDetail({
  document,
  labelName,
  labelColor,
  onDocumentUpdate,
  onBack,
  onDelete,
  onReprocessed,
  activeTab,
  onTabChange,
  destinationCaseId,
  destinationCaseName,
  onImportSucceeded,
}: LakeFileDetailProps) {
  const [indexedData, setIndexedData] = useState<DocumentIndexedData | null>(null)
  const [indexedLoading, setIndexedLoading] = useState(false)
  const [indexedError, setIndexedError] = useState<string | null>(null)
  const [chunksPage, setChunksPage] = useState(1)
  const [expandedChunkIds, setExpandedChunkIds] = useState<Set<string>>(new Set())
  const [reprocessing, setReprocessing] = useState(false)

  // Extraction scopes state
  const [scopes, setScopes] = useState<ExtractionScope[]>([])
  const [scopesLoading, setScopesLoading] = useState(false)
  const [scopesError, setScopesError] = useState<string | null>(null)

  // Parquet preview state
  const PARQUET_PAGE_SIZE = 100
  const [parquetSections, setParquetSections] = useState<ParquetSection[]>([])
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0)
  const [parquetColumns, setParquetColumns] = useState<TabularColumn[]>([])
  const [parquetRows, setParquetRows] = useState<(string | number | boolean | null)[][]>([])
  const [parquetTotalRows, setParquetTotalRows] = useState(0)
  const [parquetFilteredRows, setParquetFilteredRows] = useState(0)
  const [parquetCurrentOffset, setParquetCurrentOffset] = useState(0)
  const [parquetLoading, setParquetLoading] = useState(false)
  const [parquetNavigating, setParquetNavigating] = useState(false)
  const [parquetError, setParquetError] = useState<string | null>(null)
  const [parquetActiveQuery, setParquetActiveQuery] = useState<TabularQueryRequest | null>(null)

  // Extraction scope modal state
  const [extractionModal, setExtractionModal] = useState<ExtractionModalState>({ open: false })

  // Smart polling: auto-refresh document while pipeline is active
  useLakeDocumentStatus({ document, onDocumentUpdate })

  const fetchIndexedData = useCallback(
    async (page: number) => {
      if (!document.id) return
      setIndexedLoading(true)
      setIndexedError(null)
      try {
        const data = await lakeApi.getDocumentIndexedData(document.id, page, 30)
        setIndexedData(data)
        setChunksPage(page)
      } catch (err) {
        setIndexedError(err instanceof Error ? err.message : 'Failed to load indexed data')
      } finally {
        setIndexedLoading(false)
      }
    },
    [document.id]
  )

  const fetchScopes = useCallback(async () => {
    if (!document.id) return
    setScopesLoading(true)
    setScopesError(null)
    try {
      const data = await extractionScopeApi.list(document.id)
      setScopes(data)
    } catch (err) {
      setScopesError(err instanceof Error ? err.message : 'Failed to load extraction scopes')
    } finally {
      setScopesLoading(false)
    }
  }, [document.id])

  const fetchParquetPreview = useCallback(
    async (sectionIndex = 0) => {
      if (!document.has_parquet) return
      setParquetLoading(true)
      setParquetError(null)
      setParquetActiveQuery(null)
      try {
        const resp = await lakeApi.getTabularPreview(
          document.id,
          0,
          PARQUET_PAGE_SIZE,
          sectionIndex
        )
        setParquetSections(resp.sections ?? [])
        setParquetColumns(resp.columns)
        setParquetRows(resp.rows)
        setParquetTotalRows(resp.total_rows)
        setParquetFilteredRows(resp.total_rows)
        setParquetCurrentOffset(0)
      } catch (err) {
        setParquetError(err instanceof Error ? err.message : 'Failed to load Parquet preview')
      } finally {
        setParquetLoading(false)
      }
    },
    [document.id, document.has_parquet]
  )

  const handleParquetQuery = useCallback(
    async (req: TabularQueryRequest) => {
      setParquetLoading(true)
      setParquetError(null)
      const queryWithPaging: TabularQueryRequest = {
        ...req,
        offset: 0,
        limit: PARQUET_PAGE_SIZE,
        section_index: selectedSectionIndex,
      }
      setParquetActiveQuery(queryWithPaging)
      try {
        const resp = await lakeApi.queryTabular(document.id, queryWithPaging)
        setParquetColumns(resp.columns)
        setParquetRows(resp.rows)
        setParquetFilteredRows(resp.total_rows)
        setParquetCurrentOffset(0)
      } catch (err) {
        setParquetError(err instanceof Error ? err.message : 'Failed to run query')
      } finally {
        setParquetLoading(false)
      }
    },
    [document.id, selectedSectionIndex]
  )

  const handleParquetPrevPage = useCallback(async () => {
    if (parquetNavigating || parquetCurrentOffset === 0) return
    const newOffset = Math.max(0, parquetCurrentOffset - PARQUET_PAGE_SIZE)
    setParquetNavigating(true)
    try {
      const req: TabularQueryRequest = {
        ...parquetActiveQuery,
        offset: newOffset,
        limit: PARQUET_PAGE_SIZE,
        section_index: selectedSectionIndex,
      }
      const resp = await lakeApi.queryTabular(document.id, req)
      setParquetRows(resp.rows)
      setParquetCurrentOffset(newOffset)
      setParquetFilteredRows(resp.total_rows)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load page')
    } finally {
      setParquetNavigating(false)
    }
  }, [
    document.id,
    parquetActiveQuery,
    parquetCurrentOffset,
    parquetNavigating,
    selectedSectionIndex,
  ])

  const handleParquetNextPage = useCallback(async () => {
    if (parquetNavigating) return
    const newOffset = parquetCurrentOffset + PARQUET_PAGE_SIZE
    setParquetNavigating(true)
    try {
      const req: TabularQueryRequest = {
        ...parquetActiveQuery,
        offset: newOffset,
        limit: PARQUET_PAGE_SIZE,
        section_index: selectedSectionIndex,
      }
      const resp = await lakeApi.queryTabular(document.id, req)
      setParquetRows(resp.rows)
      setParquetCurrentOffset(newOffset)
      setParquetFilteredRows(resp.total_rows)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load page')
    } finally {
      setParquetNavigating(false)
    }
  }, [
    document.id,
    parquetActiveQuery,
    parquetCurrentOffset,
    parquetNavigating,
    selectedSectionIndex,
  ])

  const handleDeleteScope = async (scopeId: string) => {
    try {
      await extractionScopeApi.delete(document.id, scopeId)
      toast.success('Extraction scope deleted')
      await fetchScopes()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete scope')
    }
  }

  const handleReprocess = async () => {
    setReprocessing(true)

    // Optimistically reflect the re-run on the exact fields the stepper and
    // status polling read, mirroring what LakeReprocessService persists.
    // Without this the stepper shows no change on click and fast pipelines
    // (small structured files) finish before the first poll, so the re-run
    // looks like a no-op.
    if (onDocumentUpdate) {
      onDocumentUpdate({
        ...document,
        indexing_status: 'pending',
        indexing_error: null,
        analysis_status: 'analyzing',
      })
    }

    try {
      await lakeApi.reprocessDocument(document.id, [])
      toast.success('Reindexing queued', {
        description: 'The document is being re-analyzed and re-indexed.',
      })
      onReprocessed?.()
    } catch (err) {
      if (onDocumentUpdate) {
        onDocumentUpdate(document)
      }
      toast.error('Failed to reindex', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
      })
    } finally {
      setReprocessing(false)
    }
  }

  const handleTabChange = (value: DetailTab) => {
    onTabChange(value)
  }

  // Reset state when document changes
  useEffect(() => {
    setIndexedData(null)
    setIndexedError(null)
    setChunksPage(1)
    setScopes([])
    setScopesError(null)
    setParquetSections([])
    setSelectedSectionIndex(0)
    setParquetColumns([])
    setParquetRows([])
    setParquetTotalRows(0)
    setParquetFilteredRows(0)
    setParquetCurrentOffset(0)
    setParquetError(null)
    setParquetActiveQuery(null)
  }, [])

  // Fetch data on tab activation
  useEffect(() => {
    if (activeTab === 'indexed-data') {
      fetchIndexedData(1)
    }
  }, [activeTab, fetchIndexedData])

  // The Parquet preview now lives in the Indexed Data tab (it's the indexed,
  // preprocessed representation). (Re)load it when that tab opens or the
  // selected section changes — independent of the chunks fetch above.
  useEffect(() => {
    if (activeTab === 'indexed-data' && document.has_parquet) {
      fetchParquetPreview(selectedSectionIndex)
    }
  }, [activeTab, fetchParquetPreview, document.has_parquet, selectedSectionIndex])

  useEffect(() => {
    if (activeTab === 'imports') {
      fetchScopes()
    }
  }, [activeTab, fetchScopes])

  const handlePageChange = (newPage: number) => {
    setExpandedChunkIds(new Set())
    fetchIndexedData(newPage)
  }

  const toggleChunk = (chunkId: string) => {
    setExpandedChunkIds(prev => {
      const next = new Set(prev)
      if (next.has(chunkId)) {
        next.delete(chunkId)
      } else {
        next.add(chunkId)
      }
      return next
    })
  }

  return (
    <>
      <FileDetailHeader
        document={document}
        labelName={labelName}
        labelColor={labelColor}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onDelete={onDelete}
        destinationCaseId={destinationCaseId}
        onImportToCase={() => setExtractionModal({ open: true, wholeDoc: true })}
      />

      {/* Breadcrumb — back to overview */}
      {onBack && (
        <div className="max-md:!px-4" style={{ padding: '14px 24px 0' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              padding: 0,
              fontSize: 11,
              color: 'var(--muted-foreground)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <ChevronLeft size={13} />
            <span>Data Lake Overview</span>
            <span style={{ opacity: 0.5 }}>/</span>
            <span
              style={{
                color: 'var(--foreground)',
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {document.filename}
            </span>
          </button>
        </div>
      )}

      {activeTab === 'analysis' && (
        <OverviewTab
          document={document}
          reprocessing={reprocessing}
          onReprocess={handleReprocess}
        />
      )}

      {activeTab === 'indexed-data' && (
        <IndexedDataTab
          document={document}
          indexedData={indexedData}
          indexedLoading={indexedLoading}
          indexedError={indexedError}
          chunksPage={chunksPage}
          expandedChunkIds={expandedChunkIds}
          onPageChange={handlePageChange}
          onToggleChunk={toggleChunk}
          parquetSections={parquetSections}
          selectedSectionIndex={selectedSectionIndex}
          onSelectSection={setSelectedSectionIndex}
          parquetColumns={parquetColumns}
          parquetRows={parquetRows}
          parquetTotalRows={parquetTotalRows}
          parquetFilteredRows={parquetFilteredRows}
          parquetCurrentOffset={parquetCurrentOffset}
          parquetLoading={parquetLoading}
          parquetNavigating={parquetNavigating}
          parquetError={parquetError}
          onParquetQuery={handleParquetQuery}
          onParquetPrevPage={handleParquetPrevPage}
          onParquetNextPage={handleParquetNextPage}
          onClearParquetSection={() => {
            setParquetActiveQuery(null)
            setParquetColumns([])
            setParquetRows([])
          }}
        />
      )}

      {activeTab === 'imports' && (
        <ImportsTab
          document={document}
          scopes={scopes}
          scopesLoading={scopesLoading}
          scopesError={scopesError}
          onDeleteScope={handleDeleteScope}
          onOpenExtractionModal={opts => setExtractionModal({ open: true, ...opts })}
        />
      )}

      {activeTab === 'processing-stats' && (
        <div
          className="max-md:!px-4"
          style={{
            padding: '20px 24px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <PageTitle>Processing Stats</PageTitle>
          <ProcessingStatsTab documentId={document.id} />
        </div>
      )}

      {/* Extraction Scope Modal */}
      {extractionModal.open &&
        (STRUCTURAL_FILE_TYPES.has(document.file_type?.toLowerCase() ?? '') ? (
          <StructuralExtractionModal
            document={document}
            open={extractionModal.open}
            onClose={() => setExtractionModal({ open: false })}
            onSaved={() => {
              fetchScopes()
              if (destinationCaseId && onImportSucceeded) {
                const caseLabel = destinationCaseName ?? 'case'
                toast.success(`Imported to ${caseLabel}`)
                onImportSucceeded()
              }
            }}
          />
        ) : (
          <ExtractionScopeModal
            document={document}
            isOpen={extractionModal.open}
            onClose={() => setExtractionModal({ open: false })}
            onScopeCreated={() => {
              fetchScopes()
              if (destinationCaseId && onImportSucceeded) {
                const caseLabel = destinationCaseName ?? 'case'
                toast.success(`Imported to ${caseLabel}`)
                onImportSucceeded()
              }
            }}
            initialChunkId={extractionModal.chunkId}
            initialChunkPageNumber={extractionModal.chunkPageNumber}
            selectWholeDocument={extractionModal.wholeDoc}
            preSelectedCaseId={destinationCaseId ?? undefined}
          />
        ))}
    </>
  )
}
